// src/components/AIBorderWrapper.js
import React from "react";
import { Box } from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";

// Shared shimmer animation for AI border
const aiBorderShimmer = keyframes`
  0% {
    background-position: 0% 50%;
  }
  100% {
    background-position: 200% 50%;
  }
`;

// Static gradient: balanced mix of the 4 framework colors
const STATIC_GRADIENT =
  "linear-gradient(135deg, #ff6ec4 0%, #6366f1 25%, #22c55e 50%, #fbbf24 75%, #ff6ec4 100%)";

// Animated gradient: conic for nice shimmer
const ANIMATED_GRADIENT =
  "conic-gradient(from 180deg, #ff6ec4, #6366f1, #22c55e, #fbbf24, #ff6ec4)";

const StyledAiBorder = styled(Box, {
  shouldForwardProp: (prop) => prop !== "aiActive" && prop !== "animated",
})(({ theme, aiActive, animated }) => {
  const hasAi = !!aiActive;

  return {
    position: "relative",
    borderRadius: theme.shape.borderRadius * 2,
    padding: 2, // thickness of the gradient border

    // Use animated conic gradient when animated, else static multi-color linear gradient
    background: hasAi
      ? animated
        ? ANIMATED_GRADIENT
        : STATIC_GRADIENT
      : theme.palette.divider,

    backgroundSize: animated ? "200% 200%" : "100% 100%",
    animation:
      hasAi && animated ? `${aiBorderShimmer} 6s linear infinite` : "none",

    // Multi-color glow around the card
    boxShadow: hasAi
      ? [
          "0 0 10px rgba(255,110,196,0.45)", // pink
          "0 0 14px rgba(99,102,241,0.40)",  // indigo/blue
          "0 0 18px rgba(34,197,94,0.35)",   // green
          "0 0 22px rgba(251,191,36,0.35)",  // amber/gold
        ].join(", ")
      : "none",
  };
});

/**
 * AiBorderWrapper
 *
 * Props:
 *  - aiActive: boolean   // whether to show “AI zone” border
 *  - animated: boolean   // shimmer on/off
 */
export default function AiBorderWrapper({
  aiActive = false,
  animated = false,
  children,
  ...rest
}) {
  return (
    <StyledAiBorder aiActive={aiActive} animated={animated} {...rest}>
      {children}
    </StyledAiBorder>
  );
}
